import React, { useEffect, useState } from "react";
import { Modal, Title, Text, Stack } from "@mantine/core";
import { useProducts } from "../../context/ProductsContext";
import { useApp } from "../../context/AppContext";
import { useSalon } from "../../context/SalonContext";
import ProductForm from "./ProductForm";
import TaxRateMultiSelect from "../settings/TaxRateMultiSelect";

export default function EditProductModal({
  opened,
  onClose,
  product,
  readOnly = false,
}) {
  const { updateProduct } = useProducts();
  const { getTaxRates, setProductTaxRates } = useApp();
  const { businessInfo } = useSalon();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [modalTitle, setModalTitle] = useState("Edit Product");
  const [selectedTaxRateIds, setSelectedTaxRateIds] = useState([]);

  const handleModalTitleChange = (title) => {
    setModalTitle(title);
  };

  // Load the current tax rate linkage for this product whenever the modal
  // opens. We look it up by walking the salon's tax rate list and checking
  // which rates reference this product — the backend already serializes the
  // product_ids array on each tax rate in GET /business-locations/{id}/tax-rates.
  useEffect(() => {
    if (!opened || !businessInfo?.id || !product?.id) {
      setSelectedTaxRateIds([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const rates = await getTaxRates(businessInfo.id);
        if (cancelled) return;
        const linked = (Array.isArray(rates) ? rates : [])
          .filter((r) => r.product_ids?.includes(product.id))
          .map((r) => r.id);
        setSelectedTaxRateIds(linked);
      } catch (err) {
        console.error("Failed to load current tax rates for product:", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [opened, product?.id, businessInfo?.id, getTaxRates]);

  const handleSubmit = async (productData) => {
    try {
      setIsSubmitting(true);
      await updateProduct(product.id, productData);
      // Phase 2: persist the tax rate junctions after the product itself saves.
      // Separate call so the backend bulk-mode recalc pass only fires once.
      await setProductTaxRates(product.id, selectedTaxRateIds);
      onClose();
    } catch (error) {
      console.error("Error updating product:", error);
      // Error handling is done in the context
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  if (!product) {
    return null;
  }

  return (
    <Modal
      opened={opened}
      onClose={handleCancel}
      title={<Title order={3}>{modalTitle}</Title>}
      size="xl"
      closeOnClickOutside={!isSubmitting}
      closeOnEscape={!isSubmitting}
    >
      <Text size="sm" color="dimmed" mb="md">
        Update the product details below. Only modified fields will be updated.
      </Text>

      <Stack gap="md">
        <ProductForm
          mode="edit"
          product={product}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          isSubmitting={isSubmitting}
          setIsSubmitting={setIsSubmitting}
          onModalTitleChange={handleModalTitleChange}
          readOnly={readOnly}
        />
        <TaxRateMultiSelect
          value={selectedTaxRateIds}
          onChange={setSelectedTaxRateIds}
          disabled={readOnly || isSubmitting}
        />
      </Stack>
    </Modal>
  );
}
